import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable, Optional } from "@angular/core";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { EnvironmentConfig, AuthenticationService } from "../core/core";
import { ShopCart, ShopOrder, CommerceDraftEdit, CommerceCheckoutLink, CommerceRewardsBalance, CommercePage, ShopSavedCart } from "./commerce.model";

declare const environment: EnvironmentConfig;

@Injectable({ providedIn: "root" })
export class ShopCartService {
  readonly cart$ = new BehaviorSubject<ShopCart>(null);
  private readonly endpoint = environment.ordersApiDomain + "/api/orders/v2/shop";
  private readonly storageKey = "rubbl.shop.cart.v1";
  private identity: { id: string; token: string };

  constructor(private http: HttpClient, @Optional() private auth?: AuthenticationService) {
    try { this.identity = JSON.parse(localStorage.getItem(this.storageKey) || "null"); } catch { this.identity = null; }
  }

  load(): Observable<ShopCart> {
    if (!this.identity?.id || !this.identity?.token) return of(null);
    return this.http.get<ShopCart>(this.endpoint + "/carts/" + encodeURIComponent(this.identity.id), { headers: this.headers() })
      .pipe(map(cart => this.requireCart(cart)), tap(cart => this.cart$.next(cart)));
  }

  restore(id: string, token: string): Observable<ShopCart> {
    if (!id || id.length > 64 || !token || token.length > 128) return throwError(() => new Error("This cart recovery link is invalid."));
    return this.http.get<ShopCart>(this.endpoint + "/carts/" + encodeURIComponent(id),
      { headers: this.headers().set("X-Commerce-Cart-Token", token) }).pipe(map(cart => {
        this.requireCart(cart, false);
        if (cart.id !== id) throw new Error("The server did not confirm this cart recovery link.");
        this.identity = { id, token };
        try { localStorage.setItem(this.storageKey, JSON.stringify(this.identity)); } catch { /* Keep the capability in this session. */ }
        this.cart$.next(cart);
        return cart;
      }));
  }

  add(offerId: string, quantity: number, configuration: Record<string, string>): Observable<ShopCart> {
    if (!offerId || !Number.isInteger(quantity) || quantity < 1) return throwError(() => new Error("Select a valid product and quantity."));
    configuration = Object.keys(configuration || {}).reduce((values, key) => { values[key] = String(configuration[key] ?? "").trim(); return values; }, {} as Record<string, string>);
    return (this.identity ? this.load() : this.create()).pipe(switchMap(cart => (cart?.status === "Converted" || cart?.isDraft) ? this.create() : of(cart)), switchMap(cart => {
      if (!cart) return throwError(() => new Error("Your Shop cart could not be loaded."));
      const lines = cart.lines.map(line => ({ offerId: line.offerId, quantity: line.quantity, configuration: line.configuration || {} }));
      // Different machine fitment and dimensions remain distinct lines, even for the same supplier SKU.
      const key = this.configurationKey(configuration);
      const match = lines.find(line => line.offerId === offerId && this.configurationKey(line.configuration) === key);
      if (match) match.quantity += quantity;
      else lines.push({ offerId, quantity, configuration: { ...configuration } });
      return this.update({ revision: cart.revision, customer: cart.customer, shippingAddress: cart.shippingAddress, lines });
    }));
  }

  // Dismiss only the browser's reviewed quote. Keep its fixed prices, approval and secure link intact.
  dismissQuote(): boolean {
    const quote = this.cart$.value;
    if (!quote?.isDraft || quote.status === "Converted" || quote.id !== this.identity?.id) return false;
    try {
      const saved = JSON.parse(localStorage.getItem(this.storageKey) || "null");
      if (saved?.id && saved.id !== quote.id) return false; // Another tab opened a different cart.
      localStorage.removeItem(this.storageKey);
    } catch { return false; }
    this.identity = null;
    this.cart$.next(null);
    return true;
  }

  removeLine(lineId: string, revision: number): Observable<ShopCart> {
    if (!this.identity?.id || !lineId) return throwError(() => new Error("Reload your cart before removing an item."));
    return this.http.delete<ShopCart>(this.endpoint + "/carts/" + encodeURIComponent(this.identity.id) + "/lines/" + encodeURIComponent(lineId),
      { headers: this.headers(), params: new HttpParams().set("revision", revision) })
      .pipe(map(cart => this.requireCart(cart)), tap(cart => this.cart$.next(cart)));
  }

  update(selection: CommerceDraftEdit): Observable<ShopCart> {
    if (!this.identity?.id) return throwError(() => new Error("Start a Shop cart before saving."));
    // Explicit selection projection prevents customer totals and any stale quote entering the update body.
    const body = { revision: selection.revision, customer: selection.customer, shippingAddress: selection.shippingAddress,
      lines: selection.lines.map(line => ({ offerId: line.offerId, quantity: line.quantity, configuration: line.configuration || {} })),
      notes: selection.notes, customerPoNumber: selection.customerPoNumber };
    return this.http.put<ShopCart>(this.endpoint + "/carts/" + encodeURIComponent(this.identity.id), body, { headers: this.headers() })
      .pipe(map(cart => this.requireCart(cart)), tap(cart => this.cart$.next(cart)));
  }

  checkout(revision: number): Observable<CommerceCheckoutLink> {
    if (!this.identity?.id) return throwError(() => new Error("Your Shop cart is unavailable."));
    return this.http.post<CommerceCheckoutLink>(this.endpoint + "/carts/" + encodeURIComponent(this.identity.id) + "/checkout", { revision }, { headers: this.headers() });
  }

  quote(revision: number, rewardsPoints = 0): Observable<ShopCart> {
    if (!this.identity?.id) return throwError(() => new Error("Your Shop cart is unavailable."));
    if (!Number.isInteger(rewardsPoints) || rewardsPoints < 0) return throwError(() => new Error("Enter a whole number of rewards points."));
    return this.http.post<ShopCart>(this.endpoint + "/carts/" + encodeURIComponent(this.identity.id) + "/quote", { revision, rewardsPoints }, { headers: this.headers() })
      .pipe(map(cart => this.requireCart(cart)), tap(cart => this.cart$.next(cart)));
  }

  arrange(revision: number, operationId: string, choices: {lineId: string; choice: string}[]): Observable<ShopCart> {
    if (!this.identity?.id) return throwError(() => new Error("Your Shop cart is unavailable."));
    return this.http.post<ShopCart>(this.endpoint + "/carts/" + encodeURIComponent(this.identity.id) + "/arrange", {revision, operationId, choices}, {headers: this.headers()})
      .pipe(map(cart => this.requireCart(cart)), tap(cart => this.cart$.next(cart)));
  }
  saved(continuationToken = ""): Observable<CommercePage<ShopSavedCart>> {
    return this.http.get<CommercePage<ShopSavedCart>>(this.endpoint + "/saved", {headers: this.headers(), params: new HttpParams().set("continuationToken", continuationToken)});
  }
  restoreSaved(sourceCartId: string, savedLineId: string): Observable<ShopCart> {
    return (this.identity ? this.load() : this.create()).pipe(
      switchMap(cart => !cart || cart.status === "Converted" || cart.isDraft ? this.create() : of(cart)),
      switchMap(cart => this.http.post<ShopCart>(this.endpoint + "/carts/" + encodeURIComponent(cart.id) + "/restore-saved",
        {sourceCartId, savedLineId}, {headers:this.headers()})),
      map(cart => this.requireCart(cart)), tap(cart => this.cart$.next(cart)));
  }
  openFinancing(cartId: string, requestId: string): Observable<{applicationUrl: string}> {
    return this.http.post<{applicationUrl: string}>(this.endpoint + "/carts/" + encodeURIComponent(cartId) + "/financing/" + encodeURIComponent(requestId) + "/open", {}, {headers: this.headers()});
  }
  checkoutProfile(): Observable<any> {
    return this.http.get(this.endpoint + "/checkout-profile", { headers: this.headers() });
  }

  rewards(): Observable<CommerceRewardsBalance> {
    return this.http.get<CommerceRewardsBalance>(this.endpoint + "/rewards", { headers: this.headers() });
  }

  orders(continuationToken = ""): Observable<CommercePage<ShopOrder>> {
    return this.http.get<CommercePage<ShopOrder>>(this.endpoint + "/orders", { headers: this.headers(),
      params: new HttpParams().set("continuationToken", continuationToken).set("take", "20") });
  }

  rememberOrderToken(id: string, token: string): void {
    if (!id || !token) return;
    try { localStorage.setItem("rubbl.shop.order." + id, token); } catch { /* Session can still use the token held in memory by the page. */ }
  }

  order(id: string, suppliedToken?: string): Observable<ShopOrder> {
    let token = suppliedToken;
    if (!token) { try { token = localStorage.getItem("rubbl.shop.order." + id); } catch { token = null; } }
    if (!token) token = this.identity?.token;
    if (!token && !this.auth?.user$?.value?.access_token) return throwError(() => new Error("Sign in or open the secure order link to view this order."));
    return this.http.get<ShopOrder>(this.endpoint + "/orders/" + encodeURIComponent(id), { headers: this.headers().set("X-Commerce-Cart-Token", token || "") });
  }

  reconcileTestOrder(id: string, suppliedToken?: string): Observable<ShopOrder> {
    let token = suppliedToken;
    if (!token) { try { token = localStorage.getItem("rubbl.shop.order." + id); } catch { token = null; } }
    if (!token) token = this.identity?.token;
    if (!token && !this.auth?.user$?.value?.access_token) return throwError(() => new Error("Sign in or open the secure order link to verify this test payment."));
    return this.http.post<ShopOrder>(this.endpoint + "/orders/" + encodeURIComponent(id) + "/reconcile", {},
      { headers: this.headers().set("X-Commerce-Cart-Token", token || "") });
  }

  private create(): Observable<ShopCart> {
    return this.http.post<ShopCart & { accessToken: string }>(this.endpoint + "/carts", {}, { headers: this.headers() }).pipe(map(response => {
      if (!response?.accessToken) throw new Error("Shop cart access could not be established.");
      this.requireCart(response, false);
      this.identity = { id: response.id, token: response.accessToken };
      try { localStorage.setItem(this.storageKey, JSON.stringify(this.identity)); } catch { /* Current page retains token in memory. */ }
      const { accessToken, ...cart } = response;
      this.cart$.next(cart as ShopCart);
      return cart as ShopCart;
    }));
  }

  private requireCart(cart: ShopCart, matchIdentity = true): ShopCart {
    if (!cart || typeof cart.id !== "string" || !cart.id || !Number.isInteger(cart.revision) || cart.revision < 1
      || !Array.isArray(cart.lines) || (matchIdentity && cart.id !== this.identity?.id)) {
      throw new Error("The cart server did not confirm your changes. Please try again.");
    }
    return cart;
  }

  private headers(): HttpHeaders {
    let headers = new HttpHeaders({ "X-Commerce-Cart-Token": this.identity?.token || "" });
    const user = this.auth?.user$?.value;
    if (user?.access_token && !user.expired) headers = headers.set("Authorization", "Bearer " + user.access_token);
    return headers;
  }
  private configurationKey(configuration: Record<string, string>): string {
    return JSON.stringify(Object.keys(configuration || {}).sort().map(key => [key, configuration[key]]));
  }
}
